import cx from 'classnames';
import {useFormikContext} from 'formik';
import {filter, includes, isFunction, map, some} from 'lodash';
import {Fragment, useCallback, useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';

import {ClientInfoControls, Icon, SessionParticipantRoleSelector, ParticipantIcon, UserSelectorFormik} from 'components';
import {ValidationCode} from 'enums';
import {AccountAssignmentInfo, AnyParticipant, UserParticipant} from 'interfaces';
import {TRANSLATION_KEYS} from 'locale';
import {InviteeParticipantRole, ParticipantRole, UserParticipantRole, UserSummary} from 'types';
import {apiUtils, sessionUtils} from 'utils';

const T_KEYS = TRANSLATION_KEYS.COMMON.SESSION.LABELS;

type Props = {
    basePath?: string;
    participants: AnyParticipant[],
    assignment: AccountAssignmentInfo;
    maxParticipantsByRole?: Partial<{[key in ParticipantRole]: number}>;
    maxParticipants: number;
    users?: UserSummary[];
    assistantEnabled: boolean;
    guestEnabled: boolean;
    errors?: Record<string, string>;
    hideDisabledRoles?: boolean;
    prefillSingleParticipant?: boolean;
    showParticipantRole?: boolean;
    onAddParticipant: (role: ParticipantRole) => void;
    onRemoveParticipant: (index: number) => void;
    canRemoveParticipant?: (index: number) => boolean;
};

export const EditableSessionParticipants: React.FC<Props> = ({
    participants, basePath = 'participants', assignment, maxParticipantsByRole, maxParticipants, users, hideDisabledRoles,
    showParticipantRole, assistantEnabled, guestEnabled, prefillSingleParticipant, onAddParticipant, onRemoveParticipant,
    canRemoveParticipant,
}: Props) => {
    const {t} = useTranslation();
    const {setFieldError} = useFormikContext();

    const [role, setRole] = useState<ParticipantRole>(null);
    const [initialized, setInitialized] = useState(false);

    const isParticipantCountExceeded = useCallback((_role: ParticipantRole) =>
        filter(participants, {role: _role}).length >= (maxParticipantsByRole?.[_role] ?? 0), [participants, maxParticipantsByRole]);

    const isTotalParticipantsExceeded = useMemo(
        () => participants.length >= maxParticipants,
        [participants, maxParticipants]
    );

    const addParticipantDisabled = useMemo(
        () => !role || isParticipantCountExceeded(role) || isTotalParticipantsExceeded,
        [role, isParticipantCountExceeded, isTotalParticipantsExceeded]
    );

    const agentsExceeded = useMemo(() => isParticipantCountExceeded(ParticipantRole.Agent), [isParticipantCountExceeded]);
    const clientsExceeded = useMemo(() => isParticipantCountExceeded(ParticipantRole.Client), [isParticipantCountExceeded]);
    const assistantsExceeded = useMemo(() => isParticipantCountExceeded(ParticipantRole.Assistant), [isParticipantCountExceeded]);
    const guestsExceeded = useMemo(() => isParticipantCountExceeded(ParticipantRole.Guest), [isParticipantCountExceeded]);

    const availableRoles = useMemo(() => [
        ...(!agentsExceeded && !isTotalParticipantsExceeded ? [ParticipantRole.Agent] : []),
        ...(!clientsExceeded && !isTotalParticipantsExceeded ? [ParticipantRole.Client] : []),
        ...(!assistantsExceeded && !isTotalParticipantsExceeded ? [ParticipantRole.Assistant] : []),
        ...(!guestsExceeded && !isTotalParticipantsExceeded ? [ParticipantRole.Guest] : []),
    ], [agentsExceeded, clientsExceeded, guestsExceeded, assistantsExceeded, isTotalParticipantsExceeded]);

    const agents = useMemo(() => filter(users, (user) => includes(user.inSessionRoles, UserParticipantRole.Agent)), [users]);
    const assistants = useMemo(() => filter(users, (user) => includes(user.inSessionRoles, UserParticipantRole.Assistant)), [users]);

    const handleAddParticipant = useCallback(() => {
        onAddParticipant(role);
        setRole(null);
    }, [role, onAddParticipant]);

    const handleUserChanged = useCallback((value: string, index: number) => {
        const hasSameUser = some(participants, (p, i) => i !== index && (p as UserParticipant).data?.eid === value);
        if (hasSameUser) {
            // wait setValue will be finished
            setTimeout(() => {
                setFieldError(`${basePath}[${index}].data.eid`, apiUtils.getValidationCodeTKey(t, ValidationCode.Duplicated));
            });
        }
    }, [participants, setFieldError, basePath, t]);

    const renderParticipantRole = useCallback((participantRole: ParticipantRole) => {
        if (participantRole === ParticipantRole.Agent) {
            return t(T_KEYS.AGENT);
        } else if (participantRole === ParticipantRole.Assistant) {
            return t(T_KEYS.ASSISTANT);
        } else if (participantRole === ParticipantRole.Client) {
            return t(T_KEYS.CLIENT);
        } else if (participantRole === ParticipantRole.Guest) {
            return t(T_KEYS.GUEST);
        }
    }, [t]);

    useEffect(() => {
        if (prefillSingleParticipant && availableRoles.length === 1 && !participants.length && !initialized) {
            setRole(availableRoles[0]);
            setTimeout(() => {
                setInitialized(true);
                handleAddParticipant();
            });
        }
    }, [availableRoles, handleAddParticipant, participants, prefillSingleParticipant, initialized]);

    return (
        <Fragment>
            <div>{t(T_KEYS.PARTICIPANTS)}</div>
            <div className='tw-flex tw-items-end'>
                <SessionParticipantRoleSelector
                    className='tw-flex tw-mt-4'
                    role={role}
                    roles={availableRoles}
                    guestEnabled={guestEnabled}
                    assistantEnabled={assistantEnabled}
                    hideDisabledRoles={hideDisabledRoles}
                    onChange={setRole}
                />
                {(!hideDisabledRoles || !!availableRoles.length) &&
                    <div className='tw-ml-4'>
                        <Icon
                            button
                            name='plus-circle'
                            size='md'
                            disabled={addParticipantDisabled}
                            onClick={handleAddParticipant}
                        />
                    </div>}
            </div>
            <div>
                {map(participants, (p, index) => (
                    <div key={index} className='tw-mt-4'>
                        {showParticipantRole &&
                            <div className='tw-ts-h5-bold'>
                                {renderParticipantRole(p.role)}
                            </div>}
                        <div className='tw-flex'>
                            <div className={cx('tw-flex tw-w-14 tw-mt-5 tw-items-start', {
                                'tw-mt-8': sessionUtils.isInviteeParticipant(p)
                            })}
                            >
                                <div className='tw-flex tw-items-center'>
                                    {`#${index + 1}`}
                                    <ParticipantIcon role={p.role} className='tw-ml-1' size='lg' />
                                </div>
                            </div>
                            <div className='tw-flex-grow'>
                                {p.role === ParticipantRole.Agent &&
                                    <UserSelectorFormik
                                        name={`${basePath}[${index}].data.eid`}
                                        showEmptyOption={false}
                                        className='tw-min-w-32'
                                        context='agent'
                                        users={agents}
                                        onChange={(value) => handleUserChanged(value, index)}
                                    />}
                                {p.role === ParticipantRole.Assistant &&
                                    <UserSelectorFormik
                                        name={`${basePath}[${index}].data.eid`}
                                        showEmptyOption={false}
                                        className='tw-min-w-32'
                                        context='assistant'
                                        users={assistants}
                                        onChange={(value) => handleUserChanged(value, index)}
                                    />}
                                {p.role === ParticipantRole.Client &&
                                    <div className='tw-ml-3'>
                                        <ClientInfoControls
                                            hideTitle
                                            assignment={assignment}
                                            guestEnabled={guestEnabled}
                                            basePath={`${basePath}[${index}].data.userInfo`}
                                            role={InviteeParticipantRole.Client}
                                        />
                                    </div>}
                                {p.role === ParticipantRole.Guest &&
                                    <div className='tw-ml-3'>
                                        <ClientInfoControls
                                            hideTitle
                                            assignment={assignment}
                                            guestEnabled={guestEnabled}
                                            basePath={`${basePath}[${index}].data.userInfo`}
                                            role={InviteeParticipantRole.Guest}
                                        />
                                    </div>}
                            </div>
                            <div className={cx('tw-w-6 tw-ml-4 tw-mr-1 tw-mt-5', {
                                'tw-mt-8': sessionUtils.isInviteeParticipant(p)
                            })}
                            >
                                <Icon
                                    name='delete'
                                    button
                                    color='alert'
                                    disabled={isFunction(canRemoveParticipant) && !canRemoveParticipant(index)}
                                    onClick={() => onRemoveParticipant(index)}
                                />
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </Fragment>
    );
};